const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');

const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: List } = require('@mui/material/List');
const { default: ListItemButton } = require('@mui/material/ListItemButton');
const { default: Avatar } = require('@mui/material/Avatar');
const { default: Chip } = require('@mui/material/Chip');
const { default: CircularProgress } = require('@mui/material/CircularProgress');
const { default: ChevronRightIcon } = require('@mui/icons-material/ChevronRight');
const { default: MenuBookIcon } = require('@mui/icons-material/MenuBook');
const { alpha } = require('@mui/material/styles');

const { getDisplayImageUrl: getResourceMediaUrl } = require('utils/image');

const internals = {};

module.exports = function ResourcesPage(props) {

    const { resources, isLoading, onNavigateToDetail } = props;

    const {
        Header,
        TagLine,
        ResourceCard
    } = internals;

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!resources || resources.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2, color: 'text.secondary' }}>
                <MenuBookIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                <Typography variant='h6'>No resources available right now</Typography>
                <Typography variant='body2' sx={{ opacity: 0.6 }}>Check back soon for campus tours, financial aid, housing, and more.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <Header>
                <MenuBookIcon sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant='h6' sx={{ fontWeight: 700, color: 'inherit' }}>
                    Resources
                </Typography>
            </Header>
            <TagLine>
                <Typography variant='body2'>
                    Helpful resources curated especially for you.
                </Typography>
            </TagLine>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <List disablePadding>
                    {resources.map((resource) => (
                        <ResourceCard
                            key={resource.id}
                            resource={resource}
                            onNavigate={onNavigateToDetail}
                        />
                    ))}
                </List>
            </Box>
        </Box>
    );
};

module.exports.propTypes = {
    resources: T.array,
    isLoading: T.bool,
    onNavigateToDetail: T.func
};

internals.ResourceCard = function ResourceCard({ resource, onNavigate }) {

    return (
        <internals.StyledListItemButton
            onClick={() => onNavigate(resource.id)}
            divider
            $isUnread={!!resource.isUnread}
        >
            <Avatar
                src={getResourceMediaUrl(resource.brandLogoUrl)}
                alt={resource.brandName}
                variant='rounded'
                sx={{
                    width: 48,
                    height: 48,
                    mr: 2,
                    flexShrink: 0,
                    '& img': {
                        objectFit: 'contain'
                    }
                }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600, lineHeight: 1.2, minWidth: 0, flex: '0 1 auto' }} noWrap>
                        {resource.title}
                    </Typography>
                    {resource.isUnread && <internals.NewChip component='span' label='NEW' size='small' />}
                </Box>
                {resource.subtitle && (
                    <Typography variant='body2' color='text.secondary' noWrap>
                        {resource.subtitle}
                    </Typography>
                )}
                <Typography variant='caption' color='text.secondary' noWrap>
                    {resource.brandName}
                </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: 'text.secondary', flexShrink: 0, ml: 1 }} />
        </internals.StyledListItemButton>
    );
};

internals.ResourceCard.propTypes = {
    resource: T.object.isRequired,
    onNavigate: T.func.isRequired
};

internals.Header = Styled(Box)`
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: #fff;
`;

internals.TagLine = Styled(Box)`
    padding: 10px 16px;
    background-color: ${({ theme }) => theme.palette.primary.main}22;
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
    color: ${({ theme }) => theme.palette.text.secondary};
`;

internals.StyledListItemButton = Styled(ListItemButton)`
    padding: 12px 16px;
    align-items: center;
    background-color: ${({ $isUnread, theme }) => $isUnread ? alpha(theme.palette.primary.main, 0.1) : 'transparent'};
    box-shadow: ${({ $isUnread, theme }) => $isUnread ? `inset 3px 0 0 ${theme.palette.primary.main}` : 'none'};

    &:hover {
        background-color: ${({ $isUnread, theme }) => $isUnread ? alpha(theme.palette.primary.main, 0.16) : theme.palette.action.hover};
    }
`;

internals.NewChip = Styled(Chip)`
    height: 16px;
    margin-left: 8px;
    flex-shrink: 0;
    border-radius: 8px;
    background-color: ${({ theme }) => theme.palette.secondary.main};
    color: ${({ theme }) => theme.palette.secondary.contrastText};

    .MuiChip-label {
        padding: 0 4px;
        font-size: 9px;
        font-weight: 700;
        line-height: 16px;
    }
`;
